/**
 * Anonymous visitor identity for product analytics.
 *
 * Without a visitor ID every telemetry event is an anonymous page load, so
 * "app loads" can never be turned into unique visitors, bounce rate, or a real
 * visitor -> signup conversion rate. This module supplies a pseudonymous ID
 * that is attached to every frontend log line (see `lib/logger.ts`).
 *
 * Privacy tiers - the storage durability follows the user's consent choice,
 * because a *persistent* cross-session identifier is exactly the kind of
 * tracking ePrivacy/GDPR require consent for:
 *
 *  | State                        | Storage        | Scope      |
 *  |------------------------------|----------------|------------|
 *  | GPC signalled                | none (memory)  | 'ephemeral'|
 *  | Analytics consent granted    | localStorage   | 'persistent'|
 *  | No choice yet / declined     | sessionStorage | 'session'  |
 *
 * Every tier still lets us deduplicate loads by a single person, so unique
 * visitors and bounce rate work for all traffic. Only returning-visitor and
 * multi-day retention analysis require the persistent tier.
 *
 * The ID is a random UUID. It contains no personal data and is never derived
 * from an email, IP, or user id - it cannot be reversed into an identity.
 */

import { detectGpc, hasConsentFor } from './consent';

/** How durable the current visitor ID is - reported alongside the ID. */
export type VisitorIdScope = 'persistent' | 'session' | 'ephemeral';

export interface VisitorIdentity {
  visitorId: string
  visitorScope: VisitorIdScope
}

export const VISITOR_ID_STORAGE_KEY = 'purebox.visitor.id';

/** In-memory fallback so a single page view stays correlated when storage is unavailable. */
let memoryVisitorId: string | null = null;

/** Random UUID with a fallback for non-secure contexts where randomUUID is missing. */
function generateId(): string {
  try {
    const cryptoObj = globalThis.crypto;
    if (cryptoObj?.randomUUID) {
      return cryptoObj.randomUUID();
    }
    if (cryptoObj?.getRandomValues) {
      const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    // fall through to the non-crypto fallback below
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function readFrom(storage: Storage | undefined): string | null {
  try {
    return storage?.getItem(VISITOR_ID_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeTo(storage: Storage | undefined, id: string): boolean {
  try {
    storage?.setItem(VISITOR_ID_STORAGE_KEY, id);
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolves the current visitor identity, creating one on first use.
 *
 * Returns `null` on the server so log calls made during SSR stay unattributed
 * rather than inventing an ID per render.
 */
export function getVisitorIdentity(): VisitorIdentity | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // Global Privacy Control is a legally binding opt-out signal: never persist.
  if (detectGpc()) {
    memoryVisitorId = memoryVisitorId ?? generateId();
    return { visitorId: memoryVisitorId, visitorScope: 'ephemeral' };
  }

  const canPersist = hasConsentFor('analytics');
  const storage = canPersist ? window.localStorage : window.sessionStorage;
  const scope: VisitorIdScope = canPersist ? 'persistent' : 'session';

  const existing = readFrom(storage);
  if (existing) {
    return { visitorId: existing, visitorScope: scope };
  }

  // Promote an ID already issued this session so granting consent keeps the
  // same visitor rather than double-counting them as a brand new person.
  const carriedOver = canPersist ? readFrom(window.sessionStorage) : null;
  const id = carriedOver ?? memoryVisitorId ?? generateId();

  if (!writeTo(storage, id)) {
    memoryVisitorId = id;
    return { visitorId: id, visitorScope: 'ephemeral' };
  }

  memoryVisitorId = id;
  return { visitorId: id, visitorScope: scope };
}

/** Clears stored visitor IDs. Used on consent withdrawal and by tests. */
export function clearVisitorId(): void {
  memoryVisitorId = null;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(VISITOR_ID_STORAGE_KEY);
    window.sessionStorage.removeItem(VISITOR_ID_STORAGE_KEY);
  } catch {
    // Storage unavailable - the in-memory ID is already cleared.
  }
}
